import React, { useEffect, useState, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Users, GaugeCircle, Fuel, ArrowLeft, ArrowRight, Check, ShieldCheck,
  Calendar, X, ChevronLeft, ChevronRight, Zap,
} from "lucide-react";
import { api, mediaUrl } from "../lib/api";

const CAT_NAMES = { osobni: "Osobní vozy", dodavky: "Dodávky", luxusni: "Luxusní vozy", suv: "SUV / Terénní", sportovni: "Sportovní vozy", obytne: "Obytné vozy", voziky: "Vozíky a přívěsy", elektro: "Elektro vozy" };
const INCLUDED_BASE = ["Havarijní pojištění", "Asistence 24/7", "Dálniční známka ČR"];

export default function CarDetail() {
  const { id } = useParams();
  const [car, setCar] = useState(null);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(0);
  const [lightbox, setLightbox] = useState(false);

  useEffect(() => {
    setLoading(true);
    api.get(`/cars/${id}`).then((r) => setCar(r.data)).catch(() => setCar(null)).finally(() => setLoading(false));
  }, [id]);

  const gallery = useMemo(() => {
    if (!car) return [];
    const imgs = (car.images && car.images.length ? car.images : []).slice();
    if (car.image_url && !imgs.includes(car.image_url)) imgs.unshift(car.image_url);
    return imgs.length ? imgs : [""];
  }, [car]);

  const tiers = useMemo(
    () => [...(car?.price_tiers || [])].filter((t) => t.min_days && t.price_per_day).sort((a, b) => a.min_days - b.min_days),
    [car]
  );

  if (loading) return <div className="bg-black min-h-[60vh] flex items-center justify-center text-white/50">Načítám…</div>;
  if (!car) return (
    <div className="bg-black min-h-[60vh] flex flex-col items-center justify-center gap-4">
      <p className="text-white/60">Vůz nebyl nalezen.</p>
      <Link to="/vozovy-park" className="btn-gold">Zpět na vozový park</Link>
    </div>
  );

  const move = (n) => setActive((a) => (a + n + gallery.length) % gallery.length);

  return (
    <div className="bg-black" data-testid="car-detail-page">
      <div className="container-kame pt-8 pb-3">
        <p className="text-white/40 text-sm">
          <Link to="/" className="hover:text-[#B58A50]">Úvod</Link> <span className="mx-1">›</span>
          <Link to="/vozovy-park" className="hover:text-[#B58A50]"> Vozový park</Link> <span className="mx-1">›</span> {car.name}
        </p>
      </div>

      <section className="container-kame pb-16 grid lg:grid-cols-[1.2fr_1fr] gap-10">
        {/* gallery */}
        <div>
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.4 }}
            className="card-kame overflow-hidden aspect-[16/10] cursor-zoom-in relative group"
            onClick={() => setLightbox(true)}
            data-testid="detail-main-image"
          >
            <img src={mediaUrl(gallery[active])} alt={car.name} className="w-full h-full object-cover" />
            {gallery.length > 1 && (
              <>
                <button onClick={(e) => { e.stopPropagation(); move(-1); }} className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/60 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"><ChevronLeft size={22} /></button>
                <button onClick={(e) => { e.stopPropagation(); move(1); }} className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/60 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"><ChevronRight size={22} /></button>
              </>
            )}
          </motion.div>

          {gallery.length > 1 && (
            <div className="flex gap-3 mt-3 flex-wrap" data-testid="detail-thumbs">
              {gallery.map((g, i) => (
                <button
                  key={i}
                  onClick={() => setActive(i)}
                  className={`w-24 h-16 rounded-sm overflow-hidden border ${i === active ? "border-[#B58A50]" : "border-[#1f1f1f]"}`}
                  data-testid={`detail-thumb-${i}`}
                >
                  <img src={mediaUrl(g)} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* info */}
        <div>
          <p className="eyebrow">{CAT_NAMES[car.category] || "Vozidlo"}</p>
          <h1 className="font-display text-4xl text-white uppercase mt-2">{car.name}</h1>
          <p className="text-white/50 mt-1">{[car.engine, car.transmission, car.fuel].filter(Boolean).join(" | ")}</p>

          <div className="flex flex-wrap gap-2 mt-5">
            {car.seats > 0 && <Spec icon={Users} label={`${car.seats} míst`} />}
            {car.transmission && <Spec icon={GaugeCircle} label={car.transmission} />}
            {car.fuel && <Spec icon={Fuel} label={car.fuel} />}
            {car.power_kw > 0 && <Spec icon={Zap} label={`${car.power_kw} kW (${Math.round(car.power_kw * 1.35962)} k)`} />}
            {car.year > 0 && <Spec icon={Calendar} label={`Rok ${car.year}`} />}
          </div>

          <div className="card-kame p-5 mt-6">
            <div className="flex items-end justify-between">
              <div>
                <p className="text-white/50 text-xs uppercase tracking-wider">od</p>
                <p className="font-display text-3xl text-[#B58A50] leading-none">
                  {car.price_per_day.toLocaleString("cs-CZ")} Kč
                  <span className="text-white/50 text-sm font-sans font-normal"> / den</span>
                </p>
                <p className="text-white/35 text-[11px] mt-1">bez DPH</p>
              </div>
              <Link to={`/rezervace?car=${car.id}`} className="btn-gold" data-testid="detail-reserve">
                <Calendar size={18} /> Rezervovat
              </Link>
            </div>

            <div className="mt-4 pt-4 border-t border-[#1c1c1c] flex items-center justify-between" data-testid="detail-deposit">
              <span className="text-white/60 text-sm">Vratná kauce</span>
              <span className="font-display text-lg text-white">{(car.deposit ?? 10000).toLocaleString("cs-CZ")} Kč</span>
            </div>

            {tiers.length > 0 && (
              <div className="mt-5 pt-4 border-t border-[#1c1c1c]" data-testid="detail-tiers">
                <p className="text-white/60 text-xs uppercase tracking-wider mb-2">Zvýhodněné ceny za delší pronájem</p>
                <div className="flex flex-wrap gap-2">
                  {tiers.map((t, i) => (
                    <span key={i} className="text-xs bg-[#151515] border border-[#1f1f1f] text-[#B58A50] px-2.5 py-1 rounded-sm">
                      od {t.min_days} dní: {t.price_per_day.toLocaleString("cs-CZ")} Kč/den
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>

          {car.description && (
            <div className="mt-6">
              <h2 className="font-display text-lg text-white uppercase mb-2">Popis vozu</h2>
              <p className="text-white/70 text-sm leading-relaxed">{car.description}</p>
            </div>
          )}

          <div className="mt-6">
            <h2 className="font-display text-lg text-white uppercase mb-3">V ceně pronájmu</h2>
            <div className="grid grid-cols-2 gap-2">
              {[car.mileage_limit > 0 ? `Nájezd ${car.mileage_limit} km/den` : "Nájezd 300–500 km/den", ...INCLUDED_BASE].map((i) => (
                <span key={i} className="flex items-center gap-2 text-white/70 text-sm">
                  <Check size={16} className="text-[#B58A50] shrink-0" /> {i}
                </span>
              ))}
            </div>
          </div>

          <Link to="/vozovy-park" className="btn-outline-gold mt-8 inline-flex" data-testid="detail-back">
            <ArrowLeft size={18} /> Zpět na vozový park
          </Link>
        </div>
      </section>

      {lightbox && (
        <div className="fixed inset-0 z-[60] bg-black/95 flex items-center justify-center p-4" onClick={() => setLightbox(false)} data-testid="detail-lightbox">
          <button className="absolute top-5 right-5 text-white/70 hover:text-white"><X size={30} /></button>
          <img src={mediaUrl(gallery[active])} alt={car.name} className="max-w-full max-h-full object-contain" onClick={(e) => e.stopPropagation()} />
          {gallery.length > 1 && (
            <>
              <button onClick={(e) => { e.stopPropagation(); move(-1); }} className="absolute left-5 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/10 text-white flex items-center justify-center"><ChevronLeft size={26} /></button>
              <button onClick={(e) => { e.stopPropagation(); move(1); }} className="absolute right-5 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/10 text-white flex items-center justify-center"><ChevronRight size={26} /></button>
            </>
          )}
        </div>
      )}
    </div>
  );
}

const Spec = ({ icon: Icon, label }) => (
  <span className="inline-flex items-center gap-1.5 text-sm text-white/75 bg-[#151515] px-3 py-1.5 rounded-sm">
    <Icon size={15} className="text-[#B58A50]" /> {label}
  </span>
);
